/**
 * Invoice-specific hooks using TanStack Query
 * Provides data fetching, caching, and mutations for invoice operations
 */

import { useQuery, useInfiniteQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { InvoicesService } from '@/api/services/InvoicesService';
import type {
  Invoice,
  TransformedInvoiceListResponse,
  GenerateInvoicesRequest,
  TransformedGenerateInvoicesResponse,
  UpdateInvoiceStatusRequest,
  PreviewInvoicesRequest,
  PreviewInvoicesResponse,
  RetryFailedInvoicesRequest,
} from '@/types/billing';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import { InvoiceTransformer } from '@/lib/fieldTransformers';
import { errorToastText, extractBackendError, translateBackendError } from '@/lib/translateBackendError';
import { transitionErrorText } from '@/lib/alreadyHandled';
import { apiErrorFromResponse } from '@/hooks/billing/fetchApiError';
import { retryUnlessClientError } from '@/lib/queryRetry';
import { invalidateMoneyViews } from '@/hooks/reconciliation/invalidateMoneyViews';

/**
 * The generated `contract_type` query-param union. Declared once here so both
 * list hooks pass it through to the service without a call-site cast.
 */
export type InvoiceContractTypeFilter = NonNullable<
  Parameters<typeof InvoicesService.getApiV1Invoices>[8]
>;

// Query keys for invoices
export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  // Callers really do pass optional scalars (year/month/status/contractId) and id
  // arrays (childcareIds/groupIds/childIds) straight through, so the filter record
  // has to admit `string[]` and `undefined` — TanStack serialises both fine.
  list: (filters: Record<string, string | number | boolean | string[] | undefined>) => [...invoiceKeys.lists(), { filters }] as const,
  listInfinite: (filters: Record<string, string | number | boolean | string[] | undefined>) => [...invoiceKeys.lists(), 'infinite', { filters }] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
  splits: (invoiceId: string) => [...invoiceKeys.detail(invoiceId), 'splits'] as const,
  preview: (year: number, month: number) => [...invoiceKeys.all, 'preview', { year, month }] as const,
};

// Hook to fetch all invoices
export function useInvoices(
  year?: number,
  month?: number,
  status?: string,
  contractId?: string,
  childcareIds?: string[],
  groupIds?: string[],
  contractType?: InvoiceContractTypeFilter,
  childIds?: string[],
  limit = 50,
  offset = 0,
  include?: string[],
  options?: { enabled?: boolean }
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: invoiceKeys.list({
      year, month, status, contractId, childcareIds, groupIds, contractType, childIds, limit, offset,
      include: include?.join(',') ?? '',
    }),
    queryFn: async (): Promise<TransformedInvoiceListResponse> => {
      const response = await InvoicesService.getApiV1Invoices(
        status,        // 1st param: status
        year,          // 2nd param: billingYear
        month,         // 3rd param: billingMonth
        undefined,     // 4th param: childId (single) - not used
        contractId,    // 5th param: contractId
        undefined,     // 6th param: contactId - not used
        childcareIds,  // 7th param: childcareIds
        groupIds,      // 8th param: groupIds
        contractType,  // 9th param: contractType
        childIds,      // 10th param: childIds (array)
        limit,         // 11th param: limit
        offset,        // 12th param: offset
        include        // 13th param: include (slim by default; opt in to line_items)
      );

      // Transform the response to frontend format (camelCase). The cast is the
      // wire-type -> transformed-type conversion at the transformer boundary;
      // the in-place mutation below is unchanged.
      const transformed = response as unknown as TransformedInvoiceListResponse;
      if (transformed?.data) {
        transformed.data = InvoiceTransformer.arrayToFrontend<Invoice, Invoice>(transformed.data);
      }
      return transformed;
    },
    enabled: (options?.enabled ?? true) && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Stale-while-revalidate: keep the previous month/filter's data on screen
    // while the new page loads instead of flashing skeletons / a 0-count.
    placeholderData: keepPreviousData,
    // Fail fast: the invoices tab surfaces list-fetch errors directly, so a
    // single retry is enough, and none for a 4xx that will not change.
    retry: (failureCount, error) => retryUnlessClientError(failureCount, error, { maxRetries: 1 }),
  });
}

// Page size for the progressive load-all path (the invoice list endpoint
// validates limit at a max of 100, so this is the largest page we can request).
export const INVOICE_PAGE_SIZE = 100;

/**
 * Progressive load-all variant of useInvoices for the Invoices tab.
 *
 * The tab computes its stat cards, status tabs, and bulk-draft actions over the
 * whole (filtered) month, so it needs every row — not a single bounded page.
 * This builds on useInfiniteQuery and pages through the month at INVOICE_PAGE_SIZE
 * per request; the caller drives auto-paging (fetchNextPage while hasNextPage).
 * The slim DTO + gzip keep a full month cheap on the wire, and the list is
 * virtualized so DOM nodes stay bounded regardless of row count.
 *
 * Kept separate from useInvoices (single-page) so its other consumer — the
 * cockpit FinancePanel — is unaffected.
 */
export function useInvoicesInfinite(
  year?: number,
  month?: number,
  status?: string,
  contractId?: string,
  childcareIds?: string[],
  groupIds?: string[],
  contractType?: InvoiceContractTypeFilter,
  childIds?: string[],
  include?: string[]
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useInfiniteQuery({
    queryKey: invoiceKeys.listInfinite({
      year, month, status, contractId, childcareIds, groupIds, contractType, childIds,
      include: include?.join(',') ?? '',
    }),
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<TransformedInvoiceListResponse> => {
      const response = await InvoicesService.getApiV1Invoices(
        status,                 // 1st param: status
        year,                   // 2nd param: billingYear
        month,                  // 3rd param: billingMonth
        undefined,              // 4th param: childId (single) - not used
        contractId,             // 5th param: contractId
        undefined,              // 6th param: contactId - not used
        childcareIds,           // 7th param: childcareIds
        groupIds,               // 8th param: groupIds
        contractType,           // 9th param: contractType
        childIds,               // 10th param: childIds (array)
        INVOICE_PAGE_SIZE,      // 11th param: limit
        pageParam as number,    // 12th param: offset
        include                 // 13th param: include (slim by default)
      );

      // Same transformer boundary as useInvoices above.
      const transformed = response as unknown as TransformedInvoiceListResponse;
      if (transformed?.data) {
        transformed.data = InvoiceTransformer.arrayToFrontend<Invoice, Invoice>(transformed.data);
      }
      return transformed;
    },
    // Next offset = rows loaded so far, until we've reached total_count.
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage?.total_count ?? 0;
      const loaded = allPages.reduce((n, p) => n + (p?.data?.length ?? 0), 0);
      return loaded < total ? loaded : undefined;
    },
    enabled: isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    // Stale-while-revalidate: keep the previous month/filter's rows on screen
    // while a new month/filter loads instead of flashing skeletons / a 0-count.
    placeholderData: keepPreviousData,
    // Fail fast: the tab surfaces list-fetch errors directly.
    retry: (failureCount, error) => retryUnlessClientError(failureCount, error, { maxRetries: 1 }),
  });
}

// Hook to fetch a single invoice with details
export function useInvoice(id: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: invoiceKeys.detail(id),
    queryFn: async () => {
      const response = await InvoicesService.getApiV1Invoices1(id);
      // Transform single invoice to frontend format
      return InvoiceTransformer.toFrontend(response) as Invoice;
    },
    enabled: !!id && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to fetch invoice splits (payment distribution).
// Returns the splits array directly (snake_case as defined by billing_InvoiceSplit)
// — InvoiceSendDialog and the payment-distribution table both consume that shape.
export function useInvoiceSplits(invoiceId: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: invoiceKeys.splits(invoiceId),
    queryFn: async () => {
      const response = await InvoicesService.getApiV1InvoicesSplits(invoiceId);
      return response.splits ?? [];
    },
    enabled: !!invoiceId && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to generate invoices
export function useGenerateInvoices() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async (request: GenerateInvoicesRequest) => {
      // Transform request to backend format (snake_case)
      const backendRequest = InvoiceTransformer.toBackend(request) as GenerateInvoicesRequest;
      const response = await InvoicesService.postApiV1InvoicesGenerate(backendRequest);
      // Transform response to frontend format
      // `toFrontend` recursively camelCases every key, so the runtime shape is
      // the transformed one — not the snake_case wire DTO.
      return InvoiceTransformer.toFrontend(
        response,
      ) as TransformedGenerateInvoicesResponse;
    },
    onSuccess: async () => {
      // Generating invoices moves every billing surface: the debtor's
      // Outstanding, the account statement, the subsidy obligations grid. One
      // shared list, and it broadcasts to the operator's other tabs.
      await invalidateMoneyViews(queryClient);

      toast.success(i18n._('invoices.toast.generateSuccess'));
    },
    onError: (error: unknown) => {
      console.error('Failed to generate invoices:', error);
      const { code } = extractBackendError(error);
      toast.error(
        translateBackendError(code, i18n, msg`Failed to generate invoices. Please try again.`)
      );
    },
  });
}

// Hook to preview invoices before generation
export function usePreviewInvoices() {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async (request: PreviewInvoicesRequest): Promise<PreviewInvoicesResponse> => {
      // Transform request to backend format
      const backendRequest = InvoiceTransformer.toBackend(request) as PreviewInvoicesRequest;
      const response = await InvoicesService.postApiV1InvoicesPreview(backendRequest);
      // Transform response to frontend format. `warnings`/`previews` stay
      // deliberately untyped on PreviewInvoicesResponse — consumers must funnel
      // them through normalizeValidationWarnings() and read both casings (#1040).
      return InvoiceTransformer.toFrontend(response) as PreviewInvoicesResponse;
    },
    onError: (error: unknown) => {
      console.error('Failed to preview invoices:', error);
      const { code } = extractBackendError(error);
      toast.error(translateBackendError(code, i18n, msg`Failed to preview invoices`));
    },
  });
}

// Hook to update invoice status
// `silent` suppresses the per-call success toast. Used by batch callers (e.g.
// useCancelAndProceed) that cancel many invoices in a loop and surface a single
// consolidated toast instead of one per invoice.
export function useUpdateInvoiceStatus(options?: {
  /** No success toast: the caller reports once for a batch. */
  silent?: boolean;
}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: UpdateInvoiceStatusRequest }) => {
      // Transform status request to backend format
      const backendStatus = InvoiceTransformer.toBackend(status) as UpdateInvoiceStatusRequest;
      const response = await InvoicesService.putApiV1InvoicesStatus(id, backendStatus);
      // Transform response to frontend format
      return InvoiceTransformer.toFrontend(response) as Invoice;
    },
    onSuccess: async (data, variables) => {
      // A status change (finalize, cancel, credit) is a money event.
      await invalidateMoneyViews(queryClient);
      queryClient.setQueryData(invoiceKeys.detail(variables.id), data);
      if (!silent) {
        toast.success(i18n._('invoices.toast.statusUpdateSuccess'));
      }
    },
    onError: (error: unknown) => {
      console.error('Failed to update invoice status:', error);
      // A 404/409 means a colleague moved the invoice first ("Already handled").
      toast.error(transitionErrorText(error, i18n, msg`Failed to update invoice status`));
    },
  });
}

// Hook to send invoice by email
export function useSendInvoice() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async (id: string) => {
      // The endpoint takes a `billing_SendInvoiceEmailRequest` body; both of its
      // fields (`lang`, `payer_id`) are optional, so an empty object selects the
      // backend defaults. Previously the argument was omitted entirely, which
      // does not compile and would have posted no body at all.
      const response = await InvoicesService.postApiV1InvoicesSend(id, {});
      // Transform response if it contains invoice data
      return response ? (InvoiceTransformer.toFrontend(response) as unknown) : response;
    },
    onSuccess: async () => {
      // Sending moves the invoice out of 'finalized' into 'sent', which the
      // dunning and debtor surfaces both read.
      await invalidateMoneyViews(queryClient);
      toast.success(i18n._('invoices.toast.sendSuccess'));
    },
    onError: (error: unknown) => {
      console.error('Failed to send invoice:', error);
      toast.error(errorToastText(error, i18n, msg`Failed to send invoice`));
    },
  });
}

function getDownloadErrorMessage(i18n: ReturnType<typeof useLingui>['i18n'], code: string | undefined, fallbackMessage: string): string {
  switch (code) {
    case 'QR_IBAN_NOT_CONFIGURED':
      return i18n._({ id: 'invoices.toast.downloadFailed.qrIbanNotConfigured', message: 'Cannot generate PDF: Please configure an IBAN in your billing settings first.' });
    case 'INVOICE_NOT_FINALIZED':
      return i18n._({ id: 'invoices.toast.downloadFailed.invoiceNotFinalized', message: 'Cannot generate PDF: The invoice must be finalized first.' });
    default:
      return i18n._({ id: 'invoices.toast.downloadFailed', message: 'Failed to download invoice PDF: {errorMessage}', values: { errorMessage: fallbackMessage } });
  }
}

// Parameters for downloading an invoice PDF
interface DownloadInvoicePDFParams {
  invoiceId: string;
  payerId: string;
  lang?: string;
}

// Hook to download invoice PDF
export function useDownloadInvoicePDF() {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ invoiceId, payerId, lang }: DownloadInvoicePDFParams) => {
      const { getAuthToken } = await import('@/api/auth-helper');
      const token = await getAuthToken();
      const baseUrl = import.meta.env.VITE_API_BASE || '';
      const params = new URLSearchParams({ payer_id: payerId });
      if (lang) params.set('lang', lang);

      const response = await fetch(
        `${baseUrl}/api/v1/invoices/${invoiceId}/pdf?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw await apiErrorFromResponse(response, response.url, 'Failed to download invoice PDF');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition');
      const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `invoice-${invoiceId}.pdf`;

      return { blob, filename };
    },
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(i18n._({ id: 'invoices.toast.downloadSuccess', message: 'PDF downloaded successfully' }));
    },
    onError: (error: unknown) => {
      const { code } = extractBackendError(error);
      const detail = errorToastText(error, i18n, msg({ id: 'invoices.toast.downloadFailed.generic', message: 'Download failed' }));
      toast.error(getDownloadErrorMessage(i18n, code, detail));
    },
  });
}

// Hook to update a single field of an invoice (for table editing)
export function useUpdateInvoiceField() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ 
      id, 
      field, 
      value 
    }: { 
      id: string; 
      field: keyof Invoice; 
      value: Invoice[keyof Invoice] 
    }) => {
      // For now, we can only update the status field
      if (field === 'status') {
        const backendRequest = InvoiceTransformer.toBackend({ status: value as string }) as UpdateInvoiceStatusRequest;
        const response = await InvoicesService.putApiV1InvoicesStatus(id, backendRequest);
        return InvoiceTransformer.toFrontend(response) as Invoice;
      }
      throw new Error(i18n._(msg`Field update not supported`));
    },
    onSuccess: (data, variables) => {
      // Update the specific invoice in the cache
      queryClient.setQueryData(invoiceKeys.detail(variables.id), data);
      
      // Update the invoice in any list queries
      queryClient.setQueriesData(
        { queryKey: ['invoices', 'list'] },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((invoice: Invoice) =>
              invoice.id === variables.id ? { ...invoice, [variables.field]: variables.value } : invoice
            ),
          };
        }
      );
      
      // Show success message
      toast.success(i18n._('invoices.toast.updateSuccess'));
    },
    onError: (error: Error) => {
      throw error; // Re-throw to be handled by the table component
    },
  });
}

// Hook to retry failed invoice generation
export function useRetryFailedInvoices() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async (request: RetryFailedInvoicesRequest) => {
      // Transform request to backend format (snake_case)
      const backendRequest = InvoiceTransformer.toBackend(request) as GenerateInvoicesRequest;
      const response = await InvoicesService.postApiV1InvoicesGenerate(backendRequest);
      // Transform response to frontend format
      return InvoiceTransformer.toFrontend(
        response,
      ) as TransformedGenerateInvoicesResponse;
    },
    onSuccess: async (data: TransformedGenerateInvoicesResponse) => {
      await invalidateMoneyViews(queryClient);

      // #1156 dead-read repair: see `useGenerateInvoices` above. The failed
      // count lives in `failedContracts`, not a `failedCount` scalar.
      const message = i18n._(
        'invoices.toast.retrySuccess',
        {
          count: data.totalCreated ?? 0,
          failed: data.failedContracts?.length ?? 0,
        }
      );
      toast.success(message);
    },
    onError: (error: unknown) => {
      toast.error(errorToastText(error, i18n, i18n._('invoices.toast.retryError')));
    },
  });
}

// Helper hook to get formatted invoice data for display
export function useInvoiceWithDetails(invoiceId: string) {
  const invoiceQuery = useInvoice(invoiceId);
  const splitsQuery = useInvoiceSplits(invoiceId);

  return {
    invoice: invoiceQuery.data,
    splits: splitsQuery.data,
    isLoading: invoiceQuery.isLoading || splitsQuery.isLoading,
    error: invoiceQuery.error || splitsQuery.error,
  };
}